import { db, getDeviceId } from "../db/database";
import type { ImportBatch, ImportPreviewRow, InventoryRow } from "../types";
import { getSession } from "./auth";
import { applyMovement, getInventoryRows } from "./inventory";
import { resolveImportAdapter, type ColumnMapping, type ParsedFile } from "../imports";
import { randomId, randomUuid } from "../utils/id";
import { normalizeText } from "../utils/normalize";
import { chooseCanonicalProduct, resolveVariant } from "./variantResolver.mjs";
export type { ColumnMapping, ParsedFile } from "../imports";

export async function parseImportFile(file: File, source: ImportBatch["source"] = "generico"): Promise<ParsedFile> {
  return resolveImportAdapter(source).parse(file);
}

export async function buildImportPreview(parsed: ParsedFile, mapping: ColumnMapping): Promise<ImportPreviewRow[]> {
  if (!mapping.sku || !mapping.quantity) throw new Error("Mapeie as colunas de SKU e quantidade. A cor pode estar em uma coluna separada ou dentro do SKU.");
  const [inventory, aliases] = await Promise.all([getInventoryRows(), db.variantAliases.toArray()]);
  const entries: Array<{
    sku: string; color: string; title: string; quantity: number; order?: string;
    initial: ReturnType<typeof resolveVariant>;
  }> = [];

  for (const row of parsed.rows) {
    const sku = String(row[mapping.sku] ?? "").trim();
    const color = mapping.color ? String(row[mapping.color] ?? "").trim() : "";
    const title = mapping.title ? String(row[mapping.title] ?? "").trim() : "";
    const quantity = Number(String(row[mapping.quantity] ?? "0").replace(",", "."));
    if (!sku || !Number.isFinite(quantity) || quantity <= 0) continue;
    entries.push({
      sku, color, title, quantity,
      order: mapping.order ? String(row[mapping.order] ?? "").trim() : undefined,
      initial: resolveVariant(inventory, aliases, sku, color),
    });
  }

  type ResolvedProduct = NonNullable<(typeof entries)[number]["initial"]["product"]>;
  type TitleReference = { products: Map<string, ResolvedProduct>; variantIdsByProduct: Map<string, Set<string>> };
  const titleReferences = new Map<string, TitleReference>();
  for (const entry of entries) {
    const titleKey = normalizeText(entry.title);
    if (!titleKey || !entry.initial.product) continue;
    const reference = titleReferences.get(titleKey) ?? { products: new Map(), variantIdsByProduct: new Map() };
    reference.products.set(entry.initial.product.id, entry.initial.product);
    const variantIds = reference.variantIdsByProduct.get(entry.initial.product.id) ?? new Set<string>();
    if (entry.initial.row) variantIds.add(entry.initial.row.variant.id);
    reference.variantIdsByProduct.set(entry.initial.product.id, variantIds);
    titleReferences.set(titleKey, reference);
  }

  const canonicalTitleProduct = (reference?: TitleReference) => {
    if (!reference) return undefined;
    return chooseCanonicalProduct(Array.from(reference.products.values()));
  };

  const grouped = new Map<string, {
    sku: string; color: string; title?: string; quantity: number; order?: string; variantId?: string;
    productMatched: boolean; matchSource: "sku" | "titulo"; sourceSkus: string[];
  }>();
  for (const entry of entries) {
    let resolved = entry.initial;
    let matchSource: "sku" | "titulo" = "sku";
    const reference = titleReferences.get(normalizeText(entry.title));
    const canonicalProduct = canonicalTitleProduct(reference);
    if (canonicalProduct && (!resolved.row || resolved.product?.id !== canonicalProduct.id)) {
      const titleResolved = resolveVariant(inventory, aliases, canonicalProduct.sku, entry.color);
      let fallbackRow = titleResolved.row;
      const canonicalVariants = reference?.variantIdsByProduct.get(canonicalProduct.id) ?? new Set<string>();
      if (!fallbackRow && !entry.color && canonicalVariants.size === 1) {
        const onlyVariantId = Array.from(canonicalVariants)[0];
        fallbackRow = inventory.find((item) => item.variant.id === onlyVariantId);
      }
      if (fallbackRow) {
        resolved = { productMatched: true, product: canonicalProduct, row: fallbackRow };
        matchSource = "titulo";
      }
    }

    const key = resolved.row ? `variant:${resolved.row.variant.id}` : `raw:${normalizeText(entry.sku)}::${normalizeText(entry.color)}::${normalizeText(entry.title)}`;
    const previous = grouped.get(key);
    grouped.set(key, {
      sku: resolved.product?.sku ?? entry.sku,
      color: entry.color || previous?.color || "",
      title: entry.title || previous?.title,
      quantity: (previous?.quantity ?? 0) + entry.quantity,
      order: entry.order || previous?.order,
      variantId: resolved.row?.variant.id,
      productMatched: resolved.productMatched,
      matchSource: previous?.matchSource === "titulo" || matchSource === "titulo" ? "titulo" : "sku",
      sourceSkus: Array.from(new Set([...(previous?.sourceSkus ?? []), entry.sku])),
    });
  }

  return Array.from(grouped.values()).map((entry) => {
    const match = entry.variantId ? inventory.find((item) => item.variant.id === entry.variantId) : undefined;
    let status: ImportPreviewRow["status"] = "ok";
    if (!entry.productMatched) status = "sku_nao_encontrado";
    else if (!match) status = "cor_nao_encontrada";
    else if (match.galpao.quantity - entry.quantity < 0) status = "estoque_insuficiente";
    return {
      id: randomUuid(), sku: entry.sku, color: entry.color, quantity: entry.quantity,
      order: entry.order, title: entry.title, sourceSkus: entry.sourceSkus, matchSource: entry.matchSource,
      variantId: match?.variant.id, currentStock: match?.galpao.quantity,
      resolvedColor: match?.variant.colorName,
      newStock: match ? match.galpao.quantity - entry.quantity : undefined, status,
    };
  });
}

export async function confirmImport(
  source: ImportBatch["source"],
  parsed: ParsedFile,
  preview: ImportPreviewRow[],
): Promise<ImportBatch> {
  if (await db.imports.where("fileHash").equals(parsed.fileHash).first()) {
    throw new Error("Este arquivo parece já ter sido importado. A baixa duplicada foi bloqueada.");
  }
  const session = await getSession();
  const valid = preview.filter((row) => row.status === "ok" && row.variantId);
  if (!valid.length) throw new Error("Nenhuma linha válida para confirmar.");
  const batch: ImportBatch = {
    id: randomId("import"), source, fileName: parsed.fileName, fileHash: parsed.fileHash,
    status: "processando", orders: new Set(preview.map((row) => row.order).filter(Boolean)).size,
    items: preview.length, units: valid.reduce((sum, row) => sum + row.quantity, 0),
    userName: session?.user.name ?? "Usuário offline", createdAt: new Date().toISOString(),
    errorCount: preview.length - valid.length,
  };
  await db.imports.add(batch);
  await db.pendingSync.put({
    id: batch.id,
    operationType: "import",
    payload: { batch, rows: preview },
    deviceId: await getDeviceId(),
    userId: session?.user.id ?? "offline-user",
    status: "pendente",
    attempts: 0,
    createdAt: batch.createdAt,
    updatedAt: batch.createdAt,
  });

  let completed = 0;
  for (const row of valid) {
    try {
      await applyMovement({
        variantId: row.variantId!, warehouse: "galpao",
        type: source === "bling" ? "importacao_bling" : source === "upseller" ? "importacao_upseller" : "venda",
        quantity: -row.quantity, source: source === "bling" ? "bling" : source === "upseller" ? "upseller" : "manual",
        sourceFileId: batch.id, note: `Baixa confirmada pela importação ${parsed.fileName}`,
      });
      completed += 1;
    } catch {
      batch.errorCount += 1;
    }
  }
  batch.status = completed === valid.length && batch.errorCount === 0 ? "concluido" : completed > 0 ? "parcial" : "erro";
  await db.imports.put(batch);
  const importOperation = await db.pendingSync.get(batch.id);
  if (importOperation) await db.pendingSync.put({ ...importOperation, payload: { batch, rows: preview }, updatedAt: new Date().toISOString() });
  return batch;
}

export function suggestMapping(headers: string[], source: ImportBatch["source"] = "generico"): ColumnMapping {
  return resolveImportAdapter(source).suggestMapping(headers);
}

export function findDuplicateImport(hash: string) {
  return db.imports.where("fileHash").equals(hash).first();
}

export function summarizeImportRows(rows: InventoryRow[]) {
  return rows.reduce((total, row) => total + row.total, 0);
}
